import React, { useState, useEffect } from 'react';
import "./Financial.css"
const Financial = () => {
  const [activeService, setActiveService] = useState(0);
  const [waveOffset, setWaveOffset] = useState(0);

  const services = [
    {
      id: 0,
      title: "Wealth Management",
      desc: "Grow your assets with strategic planning and expert guidance",
      icon: "💎",
      color: "#6366f1",
      waveColor: "rgba(99, 102, 241, 0.3)"
    },
    {
      id: 1,
      title: "Retirement Planning", 
      desc: "Secure your future with comprehensive retirement strategies",
      icon: "🎯",
      color: "#f59e0b",
      waveColor: "rgba(245, 158, 11, 0.3)"
    },
    {
      id: 2,
      title: "Investment Advisory",
      desc: "Expert investment guidance for maximum returns",
      icon: "📊",
      color: "#06b6d4", 
      waveColor: "rgba(6, 182, 212, 0.3)"
    },
    {
      id: 3,
      title: "Tax Optimization",
      desc: "Minimize taxes and maximize your wealth legally",
      icon: "⚡",
      color: "#10b981",
      waveColor: "rgba(16, 185, 129, 0.3)"
    },
    {
      id: 4,
      title: "Insurance Solutions",
      desc: "Protect what matters most with smart coverage",
      icon: "🛡️",
      color: "#8b5cf6",
      waveColor: "rgba(139, 92, 246, 0.3)"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setWaveOffset(prev => (prev + 1) % 360);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{
      minHeight: '70vh',
      // background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      fontFamily: "'Inter', sans-serif",
      position: 'relative',
      overflow: 'hidden',
      marginTop : "70px"
    }}>
      

      {/* Animated Wave Background */}
      <div className="wave-bg">
        <svg 
          width="100%" 
          height="100" 
          viewBox="0 0 1000 100" 
          preserveAspectRatio="none"
        >
          <path
            d={`M0,50 Q250,${30 + Math.sin(waveOffset * 0.05) * 10} 500,50 T1000,50 V100 H0 Z`}
            fill={services[activeService].waveColor}
            style={{
              transition: 'fill 0.5s ease'
            }}
          />
        </svg>
      </div>

      <div className="container px-4 py-4">
        {/* Header */}
        <div className="text-center mb-4" style={{ animation: 'slideInWave 0.8s ease-out' }}>
          <h1 className="fw-bold mb-2" style={{
            color: '#1e293b',
            fontSize: 'clamp(2rem, 6vw, 3.5rem)',
            letterSpacing: '-1px'
          }}>
            Financial Services
          </h1>
          <div style={{
            width: '80px',
            height: '3px',
            background: `linear-gradient(90deg, ${services[activeService].color}, transparent)`,
            margin: '0 auto 1rem',
            borderRadius: '2px',
            transition: 'background 0.5s ease'
          }} />
          <p style={{
            color: '#64748b',
            fontSize: 'clamp(1rem, 3vw, 1.5rem)',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.5'
          }}>
            Transform your financial future with our expert services and innovative solutions
          </p>
        </div>

        {/* Services Flow */}
        <div className="wave-container d-flex gap-3 mb-4" style={{
          overflowX: 'auto',
          paddingBottom: '10px',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          {services.map((service, index) => (
            <div
              key={service.id}
              className={`wave-service ${activeService === index ? 'active' : ''}`}
              style={{
                minWidth: '200px',
                minHeight: '120px',
                borderRadius: '16px',
                padding: '1.5rem',
                position: 'relative',
                animation: `slideInWave 0.6s ease-out ${index * 0.1}s both`,
                flexShrink: 0
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveService(index);
                // console.log('Service clicked:', service.title, index);
              }}
            >
              <div className="d-flex align-items-start gap-3" style={{ pointerEvents: 'none' }}>
                <div
                  className="floating-icon"
                  style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: service.color,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    animationDelay: `${index * 0.2}s`
                  }}
                >
                  {service.icon}
                </div>
                <div className="flex-grow-1">
                  <h6 className="fw-bold mb-1" style={{
                    color: '#1e293b',
                    fontSize: '1.7rem',
                    lineHeight: '1.3'
                  }}>
                    {service.title}
                  </h6>
                  <p className="mb-0" style={{
                    color: '#64748b',
                    fontSize: '1.2rem',
                    lineHeight: '1.4'
                  }}>
                    {service.desc}
                  </p>
                </div>
              </div>
              
              {/* Wave indicator */}
              <div className="service-wave" style={{ color: service.color, pointerEvents: 'none' }}>
                {activeService === index && <div style={{ pointerEvents: 'none' }} />}
              </div>
            </div>
          ))}
        </div>

        {/* Active Service Details */}
        <div 
          key={activeService}
          style={{
            background: 'white',
            borderRadius: '20px',
            padding: 'clamp(1.5rem, 4vw, 2.5rem)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            animation: 'slideInWave 0.6s ease-out',
            position: 'relative'
          }}
        >
          {/* Background accent */}
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '150px',
            height: '150px',
            background: `radial-gradient(circle, ${services[activeService].color}15 0%, transparent 70%)`,
            borderRadius: '50%',
            transform: 'translate(25%, -25%)'
          }} />

          <div className="row align-items-center">
            <div className="col-md-8">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: services[activeService].color,
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2.5rem',
                    boxShadow: `0 8px 25px ${services[activeService].color}40`
                  }}
                >
                  {services[activeService].icon}
                </div>
                <div>
                  <h3 className="fw-bold mb-1" style={{
                    color: '#1e293b',
                    fontSize: 'clamp(1.3rem, 4vw, 1.8rem)'
                  }}>
                    {services[activeService].title}
                  </h3>
                  <div style={{
                    color: services[activeService].color,
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    Premium Service
                  </div>
                </div>
              </div>
              
              <p className="mb-4" style={{
                color: '#475569',
                fontSize: 'clamp(1rem, 3vw, 1.5rem)',
                lineHeight: '1.6'
              }}>
                {services[activeService].desc}. Our expert team provides personalized solutions 
                tailored to your unique financial goals and requirements.
              </p>
            </div>
            
            {/*<div className="col-md-4 text-md-end">
              <div className="d-flex flex-column flex-md-row gap-2">
                <button
                  className="service-button btn fw-bold"
                  style={{
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontSize: '1rem'
                  }}
                >
                  Get Started
                </button>
          
              </div>
            </div>*/}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Financial;